import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { ArrowLeft, CheckCircle2, Circle, Play, Square, Trash2, Plus, Clock } from 'lucide-react';

interface Todo {
  id: string;
  title: string;
  description: string | null;
  due_at: string | null;
  completed_at: string | null;
  priority: number;
  subject_code: string | null;
  started_at: string | null;
  total_work_seconds: number;
  active_session_started_at: string | null;
}

interface Subject { code: string; display_name: string | null; }

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function Timer({ start }: { start: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const sec = Math.max(0, Math.floor((now - new Date(start).getTime()) / 1000));
  return <span className="text-yellow-300 font-mono text-xs">{formatDuration(sec)}</span>;
}

const TodosPage: React.FC = () => {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [newTitle, setNewTitle] = useState('');
  const [newDueAt, setNewDueAt] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newPriority, setNewPriority] = useState(0);

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        api<{ todos: Todo[] }>(`/api/todos${showCompleted ? '?include_completed=1' : ''}`),
        api<{ subjects: Subject[] }>('/api/subjects'),
      ]);
      setTodos(t.todos);
      setSubjects(s.subjects);
    } catch (err: any) {
      setError(err?.message || 'Fehler beim Laden');
    }
  }, [showCompleted]);

  useEffect(() => { load(); }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setBusy('create');
    try {
      await api('/api/todos', {
        method: 'POST',
        body: JSON.stringify({
          title: newTitle,
          due_at: newDueAt || null,
          subject_code: newSubject || null,
          priority: newPriority,
        }),
      });
      setNewTitle(''); setNewDueAt(''); setNewSubject(''); setNewPriority(0);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Erstellen fehlgeschlagen');
    } finally { setBusy(null); }
  };

  const toggleComplete = async (todo: Todo) => {
    setBusy(todo.id);
    try {
      await api(`/api/todos/${todo.id}/complete`, {
        method: 'POST',
        body: JSON.stringify({ completed: !todo.completed_at }),
      });
      await load();
    } catch (err: any) {
      setError(err?.message || 'Fehler');
    } finally { setBusy(null); }
  };

  const toggleTimer = async (todo: Todo) => {
    setBusy(todo.id);
    try {
      if (todo.active_session_started_at) {
        await api(`/api/todos/${todo.id}/stop`, { method: 'POST' });
      } else {
        await api(`/api/todos/${todo.id}/start`, { method: 'POST' });
      }
      await load();
    } catch (err: any) {
      setError(err?.message || 'Fehler');
    } finally { setBusy(null); }
  };

  const remove = async (todo: Todo) => {
    if (!window.confirm(`„${todo.title}" wirklich löschen?`)) return;
    setBusy(todo.id);
    try {
      await api(`/api/todos/${todo.id}`, { method: 'DELETE' });
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const sortedTodos = useMemo(() => todos, [todos]);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
        <Link to="/dashboard" className="text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <CheckCircle2 className="w-6 h-6 text-green-400" />
        <h1 className="text-xl font-semibold">Aufgaben / TODOs</h1>
        <label className="ml-auto text-sm text-gray-400 flex items-center gap-2">
          <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} className="accent-blue-500" />
          Erledigte anzeigen
        </label>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
        <form onSubmit={create} className="bg-gray-800 border border-gray-700 rounded-lg p-4 flex flex-wrap gap-3">
          <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)}
            placeholder="Neue Aufgabe…"
            className="flex-1 min-w-[200px] px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg placeholder-gray-400 focus:outline-none focus:border-blue-500" />
          <input type="datetime-local" value={newDueAt} onChange={e => setNewDueAt(e.target.value)}
            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-200 text-sm" />
          <select value={newSubject} onChange={e => setNewSubject(e.target.value)}
            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-200 text-sm">
            <option value="">Fach (optional)</option>
            {subjects.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
          </select>
          <select value={newPriority} onChange={e => setNewPriority(parseInt(e.target.value))}
            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-200 text-sm">
            <option value={0}>— Prio —</option>
            <option value={1}>Niedrig</option>
            <option value={2}>Mittel</option>
            <option value={3}>Hoch</option>
          </select>
          <button type="submit" disabled={busy === 'create' || !newTitle.trim()}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2">
            <Plus className="w-4 h-4" /> Anlegen
          </button>
        </form>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <ul className="space-y-3">
          {sortedTodos.length === 0 ? (
            <p className="text-gray-500 text-sm">Noch keine Aufgaben.</p>
          ) : sortedTodos.map(t => (
            <li key={t.id} className={`bg-gray-800 border border-gray-700 rounded-lg p-4 flex items-start gap-3 ${t.completed_at ? 'opacity-60' : ''}`}>
              <button onClick={() => toggleComplete(t)} disabled={busy === t.id} className="mt-1">
                {t.completed_at
                  ? <CheckCircle2 className="w-5 h-5 text-green-400" />
                  : <Circle className="w-5 h-5 text-gray-500 hover:text-gray-300" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`font-medium ${t.completed_at ? 'line-through text-gray-400' : ''}`}>{t.title}</div>
                {t.description && <div className="text-sm text-gray-400 mt-1 whitespace-pre-wrap">{t.description}</div>}
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-400">
                  {t.due_at && <span>📅 {new Date(t.due_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</span>}
                  {t.subject_code && <span className="bg-gray-700 px-1.5 py-0.5 rounded">{t.subject_code}</span>}
                  {t.priority > 0 && <span className={t.priority === 3 ? 'text-red-400' : t.priority === 2 ? 'text-orange-400' : 'text-yellow-500'}>
                    {'!'.repeat(t.priority)}
                  </span>}
                  {t.total_work_seconds > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(t.total_work_seconds)}</span>}
                  {t.active_session_started_at && <Timer start={t.active_session_started_at} />}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!t.completed_at && (
                  <button onClick={() => toggleTimer(t)} disabled={busy === t.id}
                    className={`p-2 rounded-lg transition ${t.active_session_started_at ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-gray-700 hover:bg-gray-600'}`}
                    title={t.active_session_started_at ? 'Timer stoppen' : 'Timer starten'}>
                    {t.active_session_started_at ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                )}
                <button onClick={() => remove(t)} disabled={busy === t.id}
                  className="p-2 rounded-lg bg-gray-700 hover:bg-red-600 transition" title="Löschen">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
};

export default TodosPage;
